import { resolveElement } from '../dom/bindElements';
import { KiaAssistantError, type KiaAssistantConfig } from '../types';

export interface BoundElements {
  micElement: HTMLElement;
  outputElement: HTMLElement;
  playElement: HTMLElement;
}

/**
 * Validates and resolves the three-element contract. This is the single
 * gate all publisher-supplied config passes through before the SDK touches
 * the DOM, so element resolution errors are reported per-element.
 */
export function validateConfig(config: KiaAssistantConfig | undefined | null): BoundElements {
  if (!config || typeof config !== 'object') {
    throw new KiaAssistantError(
      'KiaAssistant.init() requires a configuration object.',
      'CONFIG_INVALID',
    );
  }

  const micElement = resolveElement(config.micElement, 'CONFIG_MIC_MISSING', 'micElement');
  const outputElement = resolveElement(
    config.outputElement,
    'CONFIG_OUTPUT_MISSING',
    'outputElement',
  );
  const playElement = resolveElement(config.playElement, 'CONFIG_PLAY_MISSING', 'playElement');

  const distinct = new Set([micElement, outputElement, playElement]);
  if (distinct.size !== 3) {
    throw new KiaAssistantError(
      'micElement, outputElement, and playElement must be three distinct DOM elements.',
      'CONFIG_ELEMENTS_NOT_DISTINCT',
    );
  }

  if (
    config.apiBaseUrl !== undefined &&
    (typeof config.apiBaseUrl !== 'string' || config.apiBaseUrl === '')
  ) {
    throw new KiaAssistantError(
      'apiBaseUrl must be a non-empty string if provided.',
      'CONFIG_INVALID',
    );
  }

  if (config.onStateChange !== undefined && typeof config.onStateChange !== 'function') {
    throw new KiaAssistantError('onStateChange must be a function if provided.', 'CONFIG_INVALID');
  }

  if (config.onError !== undefined && typeof config.onError !== 'function') {
    throw new KiaAssistantError('onError must be a function if provided.', 'CONFIG_INVALID');
  }

  return { micElement, outputElement, playElement };
}
